import { createContext, useContext, useEffect, useMemo, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme?: Theme
	storageKey?: string
}

type ThemeProviderState = {
	theme: Theme
	setTheme: (theme: Theme) => void
	resolvedTheme: Exclude<Theme, 'system'>
}

const initialState: ThemeProviderState = {
	theme: 'system',
	setTheme: () => null,
	resolvedTheme: 'light',
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'vite-ui-theme',
	...props
}: ThemeProviderProps): JSX.Element {
	const [theme, setTheme] = useState<Theme>(() => {
		try {
			return (localStorage.getItem(storageKey) as Theme) || defaultTheme
		} catch {
			return defaultTheme
		}
	})

	const [resolvedTheme, setResolvedTheme] =
		useState<Exclude<Theme, 'system'>>('light')

	useEffect(() => {
		const root = window.document.documentElement
		const media = window.matchMedia('(prefers-color-scheme: dark)')

		const applyTheme = () => {
			const systemResolved: Exclude<Theme, 'system'> = media.matches
				? 'dark'
				: 'light'
			const nextResolved: Exclude<Theme, 'system'> =
				String(theme).toLowerCase() === 'system'
					? systemResolved
					: (theme as Exclude<Theme, 'system'>)
			root.classList.remove('light', 'dark')
			root.classList.add(nextResolved)
			setResolvedTheme(nextResolved)
		}

		applyTheme()

		if (String(theme).toLowerCase() === 'system') {
			media.addEventListener('change', applyTheme)
			return () => media.removeEventListener('change', applyTheme)
		}

		return undefined
	}, [theme])

	const value = useMemo(
		() => ({
			theme,
			setTheme: (nextTheme: Theme) => {
				localStorage.setItem(storageKey, nextTheme)
				setTheme(nextTheme)
			},
			resolvedTheme,
		}),
		[theme, storageKey, resolvedTheme]
	)

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	)
}

export const useTheme = (): ThemeProviderState => {
	const context = useContext(ThemeProviderContext)

	if (context === undefined)
		throw new Error('useTheme must be used within a ThemeProvider')

	return context
}
